import {
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
  SEARCH_OPERATOR,
} from '@solinkcorp/vd-common';
import { AppDispatcher } from 'dispatcher';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import set from 'lodash/set';
import assign from 'object-assign';
import { generateSearchRegex } from 'utils/EventSearchUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import ReportUtil from 'utils/ReportUtil';
import { getQueryProps } from 'utils/RouteUtil';
import * as constants from 'vd-constants';
import { AuthStore } from './AuthStore';
import { BaseStore } from './BaseStore';
import PreferenceStore from './PreferenceStore';

// TODO: Shouldn't do this at the top level of this file.
const { capsules, reportId, search, showRevenue, searchOperator } =
  typeof window !== 'undefined' ? getQueryProps() : {};

let _eventFilterState = undefined;
let _eventSubtypeFilterState = undefined;
let _eventTagsFilter = undefined;

// The default event filters, which allow all events.
export const DEFAULT_EVENT_FITLERS = {
  bookmark: true,
  motion: true,
  transaction: true,
  saved: false,
};

function getEventFilterState() {
  if (!_eventFilterState) {
    const { showAlarms, showBookmarks, showMotion, showTransaction } = getQueryProps();
    _eventFilterState = {
      transaction: false,
      motion: false,
      bookmark: false,
      saved: false,
    };

    if (showBookmarks && showMotion && showTransaction) {
      _eventFilterState.bookmark = true;
      _eventFilterState.motion = true;
      _eventFilterState.transaction = true;
    } else if (showAlarms || showBookmarks) {
      _eventFilterState.bookmark = true;
    } else if (showMotion) {
      _eventFilterState.motion = true;
    } else if (showTransaction) {
      _eventFilterState.transaction = true;
    } else {
      _eventFilterState.bookmark = true;
      _eventFilterState.motion = true;
      _eventFilterState.transaction = !FeaturesUtil.hasNoFullAccessInKiosk();
    }
  }

  return _eventFilterState;
}

const setEventFilterState = ({
  transaction = false,
  motion = false,
  bookmark = false,
  saved = false,
}) => {
  _eventFilterState = {
    transaction,
    motion,
    bookmark,
    saved,
  };
};

const setEventSubtypeFilterState = (eventSubtypes) => {
  _eventSubtypeFilterState = {
    ...eventSubtypes,
  };
};

const setEventTagsFilter = (tags) => {
  _eventTagsFilter = tags;
};

const clearFeatureTabFilters = () => {
  _eventSubtypeFilterState = undefined;
  _eventTagsFilter = undefined;
  _eventFilterState = undefined;
};

const updateEventFilterState = (newFilters) => {
  _eventFilterState = {
    ...getEventFilterState(),
    ...newFilters,
  };
};

let _showRevenue = !isNil(showRevenue) ? showRevenue : false;
// If initializing with show revenue on, only enable transaction events
if (_showRevenue) {
  setEventFilterState({
    transaction: true,
    motion: false,
    bookmark: false,
    saved: false,
  });
}

/**
 * @type {typeof SEARCH_OPERATOR.AND | typeof SEARCH_OPERATOR.OR}
 */
let _searchOperator = searchOperator || SEARCH_OPERATOR.AND;
/**
 * @type {string}
 */
let _searchString = search || '';
let _searchStringRegex = generateSearchRegex(_searchString);
let _initialReportId = reportId || null;
/**
 * @type {Array<string>}
 */
let _capsules = capsules ? capsules.split(',') : [];
let _error = null;
let _success = null;
let _message = null;
const _realTimeReports = {};

let _reports = [];
let _selectedReport = null;
let _reportsFetched = false;

const setErrorSuccess = ({ error = null, success = null, message = null } = {}) => {
  _error = error;
  _success = success;
  _message = message;
};

const getErrorMessage = ({ error }) => {
  let message = isString(error)
    ? error
    : get(
        error,
        'response.data.error.message',
        get(error, 'response.data.error.name', error.toString()),
      );

  message = message.replace('ReportTemplate Instance is not Valid: reason=', '');
  message = message.replace('Error: ', '');
  const debugInfoIndex = message.indexOf(' [');
  if (debugInfoIndex > -1) {
    message = message.substring(0, debugInfoIndex);
  }
  return message;
};

const setSelectedFilterFromId = () => {
  if (_initialReportId) {
    // Check filters stored in DB
    if (!_selectedReport) {
      _selectedReport = _reports.find((report) => report.getTemplateId() === _initialReportId);
    }

    if (_selectedReport) {
      _initialReportId = null;

      const eventTypes = getEventTypes(_selectedReport);
      if (eventTypes) {
        const { transaction, motion, bookmark } = eventTypes;
        setEventFilterState({ transaction, motion, bookmark });
      }
    }
  }
};

const getEventTypes = (filter) => {
  let transaction = false;
  let motion = false;
  let bookmark = false;

  if (has(filter, 'filter.bool.should')) {
    filter.filter.bool.should.forEach((should) => {
      if (has(should, 'term.type')) {
        switch (should.term.type) {
          case EVENT_TYPE_MOTION: {
            motion = true;
            break;
          }
          case EVENT_TYPE_TRANSACTION: {
            transaction = true;
            break;
          }
          case EVENT_TYPE_BOOKMARK: {
            bookmark = true;
            break;
          }
        }
      }
    });
  }

  if (transaction || motion || bookmark) {
    return {
      transaction,
      motion,
      bookmark,
    };
  }
};

const resetEventTypeFilters = () => {
  _showRevenue = false;
  setEventFilterState(DEFAULT_EVENT_FITLERS);
};

const EventFilterStore = assign({}, BaseStore, {
  /**
   *
   * @returns {Array<ReportUtil>}
   */
  getReports() {
    return _reports;
  },

  /**
   * Get a new array of visible filter type reports.
   *
   * @returns {Array<ReportUtil>}
   */
  getFilters() {
    return _reports.filter((report) => report.isFilter() && report.isVisible());
  },

  /**
   * Get a new array of visible aggregation type reports.
   *
   * @returns {Array<ReportUtil>}
   */
  getAggregations() {
    return _reports.filter((report) => report.isAggregation() && report.isVisible());
  },

  /**
   *
   * @deprecated
   *
   * @returns {ReportUtil|null}
   */
  getSelectedFilter() {
    return _selectedReport;
  },

  /**
   * Get if the reports has been fetched
   * @returns {boolean}
   */
  getReportsFetched() {
    return _reportsFetched;
  },

  /**
   *
   * @returns {Object|null}
   */
  getSelectedReport() {
    const selectedId = this.getSelectedReportId();
    const locale = PreferenceStore.getLocale();
    if (selectedId) {
      return {
        id: this.getSelectedReportId({ checkInitial: true }),
        name: _selectedReport ? _selectedReport.getName(locale) : undefined,
        query: _selectedReport ? _selectedReport.getQuery() : undefined,
      };
    }
    return null;
  },

  getSelectedReportById(id) {
    return _reports.find((report) => report.templateId === id);
  },

  /**
   *
   * @returns {string|null}
   */
  getSelectedReportId({ checkInitial = false } = {}) {
    let reportTemplateId = undefined;

    if (_selectedReport) {
      reportTemplateId = _selectedReport.getId();
    }

    if (checkInitial && !reportTemplateId && _initialReportId) {
      reportTemplateId = _initialReportId;
    }

    return reportTemplateId;
  },

  /**
   *
   * @param {string} reportTemplateId - The report template ID (used to identify a report)
   * @param {string} notificationType - The real-time notification type
   * @returns {boolean|undefined}
   */
  getRealTimeReportCanEnable({ reportTemplateId, notificationType }) {
    return get(_realTimeReports, [reportTemplateId, notificationType, 'canEnable']);
  },

  /**
   *
   * @param {string} reportTemplateId
   * @param {string} notificationType
   * @returns {number|undefined}
   */
  getRealTimeReportAverage({ reportTemplateId, notificationType }) {
    return get(_realTimeReports, [reportTemplateId, notificationType, 'average']);
  },

  getAppState() {
    return {
      eventFilterState: getEventFilterState(),
      eventSubtypeFilter: !isEmpty(_eventSubtypeFilterState) ? _eventSubtypeFilterState : undefined,
      eventTagsFilter: _eventTagsFilter,
      searchString: _searchString,
      searchStringRegex: _searchStringRegex,
      showRevenue: _showRevenue,
      capsules: _capsules,
      searchOperator: _searchOperator,
      selectedReport: _selectedReport,
      selectedReportId: this.getSelectedReportId(),
    };
  },

  getShowRevenue() {
    return _showRevenue;
  },

  getError() {
    return _error;
  },

  getSuccess() {
    return _success;
  },

  getMessage() {
    return _message;
  },

  getSearchOperator() {
    return _searchOperator;
  },

  /** If there are event data field filters and freeform string search applied on the events. */
  hasDataFilters() {
    return _capsules.length > 0 || _searchString;
  },

  /**
   * Reset the state properties specific to report settings,
   * this is to prevent duplicate toast messages among pages
   */
  resetReportNotificationToastState() {
    _error = null;
    _success = null;
    _message = null;
    this.emitChange();
  },

  /**
   * Get the filter that histogram and top3 queries needed
   * from the report's metadata
   * eg. include tags, event type and eventSubtypeQueries (from feature standard)
   */
  getReportFilters(featureStandard, reportTemplateId) {
    const reportHistogramTags = [];
    const reportEventsQueryTags = [];
    const reportEventSubtypes = [];
    const reportEventSubtypeQueries = [];
    const reportEventTypes = [];
    const report = this.getSelectedReportById(reportTemplateId);
    const categories = get(report, 'metadata.categories', []);

    categories.forEach((category) => {
      const feature = featureStandard?.tabs?.[category];
      if (feature) {
        const histogramTag = get(feature, 'forceInclude.tags.histogram');
        if (histogramTag && !reportHistogramTags.includes(histogramTag)) {
          reportHistogramTags.push(histogramTag);
        }

        const eventsQueryTag = get(feature, 'forceInclude.tags.eventsQuery');
        if (eventsQueryTag && !reportEventsQueryTags.includes(eventsQueryTag)) {
          reportEventsQueryTags.push(eventsQueryTag);
        }

        const eventTypes = feature?.filter?.eventTypes || [];
        eventTypes.forEach((type) => {
          if (type && !reportEventTypes.includes(type)) {
            reportEventTypes.push(type);
          }
        });

        const subtypes = Object.keys(feature.eventSubtypes || {});
        subtypes.forEach((subtype) => {
          if (subtype && !reportEventSubtypes.includes(subtype)) {
            reportEventSubtypes.push(subtype);
          }
        });

        const eventSubtypeQuery = feature?.filter?.eventSubtypeQuery;
        if (eventSubtypeQuery && !reportEventSubtypeQueries.includes(eventSubtypeQuery)) {
          reportEventSubtypeQueries.push(eventSubtypeQuery);
        } else if (subtypes.length > 0) {
          subtypes.forEach((subtype) => {
            if (subtype && !reportEventSubtypeQueries.includes(subtype)) {
              reportEventSubtypeQueries.push(subtype);
            }
          });
        }
      }
    });

    return {
      reportEventsQueryTags,
      reportHistogramTags,
      reportEventSubtypeQueries,
      reportEventSubtypes,
      reportEventTypes,
      reportMergeWithSubtypes: report?.metadata?.mergeWithSubtypes,
    };
  },
});

EventFilterStore.dispatchToken = AppDispatcher.register((action) => {
  switch (action.type) {
    case constants.RESET_EVENT_FILTERS:
      _searchOperator = SEARCH_OPERATOR.AND;
      _capsules = [];
      _searchString = '';
      _searchStringRegex = null;
      _selectedReport = null;
      _eventFilterState = undefined;
      _eventSubtypeFilterState = undefined;
      _eventTagsFilter = undefined;
      resetEventTypeFilters();
      EventFilterStore.emitChange();
      break;

    case constants.ENABLE_SAVED_FILTER:
      setEventFilterState({
        transaction: !FeaturesUtil.hasNoFullAccessInKiosk(),
        motion: true,
        bookmark: true,
        saved: true,
      });
      EventFilterStore.emitChange();
      break;

    case constants.RESET_SAVED_FILTER:
      _searchString = '';
      _searchStringRegex = null;
      _capsules = [];
      _selectedReport = null;
      _showRevenue = false;
      setEventFilterState({
        transaction: !FeaturesUtil.hasNoFullAccessInKiosk(),
        motion: true,
        bookmark: true,
        saved: true,
      });
      EventFilterStore.emitChange();
      break;

    case constants.SET_REVENUE_FILTER: {
      let { bookmark, motion, transaction } = getEventFilterState();

      // Prevent turning off all filters, reset to initial state
      if (transaction && !motion && !bookmark && _showRevenue) {
        resetEventTypeFilters();
      } else {
        _showRevenue = true;
        transaction = true;
        motion = false;
        bookmark = false;
        updateEventFilterState({
          bookmark,
          motion,
          transaction,
        });
      }

      EventFilterStore.emitChange();
      break;
    }

    case constants.TOGGLE_REVENUE_FILTER: {
      _showRevenue = !_showRevenue;
      EventFilterStore.emitChange();
      break;
    }

    case constants.UPDATE_SEARCH_STRING:
      _searchString = action.searchString;
      _searchStringRegex = generateSearchRegex(_searchString);
      setErrorSuccess();
      EventFilterStore.emitChange();
      break;

    case constants.UPDATE_SEARCH_CAPSULES: {
      _capsules = action.capsules;
      EventFilterStore.emitChange();
      break;
    }

    case constants.CREATE_REPORT_SUCCESS: {
      const report = action.report;
      _reports.push(report);
      EventFilterStore.emitChange();

      break;
    }

    case constants.FETCH_FILTERS_SUCCESS: {
      setErrorSuccess();
      _reports = ReportUtil.parseReports(action.payload);
      _reportsFetched = true;
      // Don't reset filter if there is one set already
      const selectedFilter = EventFilterStore.getSelectedFilter();
      if (isEmpty(selectedFilter)) {
        setSelectedFilterFromId();
      }

      EventFilterStore.emitChange();
      break;
    }

    case constants.FETCH_FILTERS_ERROR: {
      _reportsFetched = true;
      EventFilterStore.emitChange();
      break;
    }

    case constants.REPORT_UPDATE_SUCCESS: {
      setErrorSuccess({ success: true });
      const idx = _reports.findIndex(
        (report) => report.getTemplateId() === action.reportTemplateId,
      );
      if (idx >= 0) {
        _reports[idx].mergeUserReportData({ report: action.report });
        EventFilterStore.emitChange();
      }
      break;
    }

    case constants.REPORT_UPDATE_ERROR:
    case constants.REPORT_TEMPLATE_UPDATE_ERROR:
    case constants.REPORT_TEMPLATE_DELETE_ERROR:
    case constants.USER_NOTIFICATION_UPDATE_ERROR:
    case constants.USER_NOTIFICATION_DELETE_ERROR: {
      const message = getErrorMessage({ error: action.error });
      setErrorSuccess({ error: action.error, message });
      EventFilterStore.emitChange();
      break;
    }

    case constants.USER_NOTIFICATION_REAL_TIME_THRESHOLD: {
      const { average, canEnable, reportTemplateId, thresholdType: notificationType } = action;
      set(_realTimeReports, [reportTemplateId, notificationType, 'canEnable'], canEnable);
      set(_realTimeReports, [reportTemplateId, notificationType, 'average'], average);

      break;
    }

    case constants.USER_NOTIFICATION_DELETE_SUCCESS: {
      setErrorSuccess({ success: true });
      const idx = _reports.findIndex(
        (report) => report.getTemplateId() === action.reportTemplateId,
      );
      if (idx >= 0) {
        _reports[idx].removeUserNotification();
        EventFilterStore.emitChange();
      }
      break;
    }

    case constants.USER_NOTIFICATION_UPDATE_SUCCESS: {
      setErrorSuccess({ success: true });
      const idx = _reports.findIndex(
        (report) => report.getTemplateId() === action.reportTemplateId,
      );
      if (idx >= 0) {
        _reports[idx].mergeUserNotificationData({ userNotification: action.payload });
        EventFilterStore.emitChange();
      }
      break;
    }

    case constants.REPORT_TEMPLATE_UPDATE_SUCCESS: {
      setErrorSuccess();
      const idx = _reports.findIndex(
        (report) => report.getTemplateId() === action.reportTemplateId,
      );
      if (idx >= 0) {
        _reports[idx].mergeTemplateData({ ...action.payload });
        EventFilterStore.emitChange();
      }
      break;
    }

    case constants.REPORT_TEMPLATE_DELETE_SUCCESS: {
      setErrorSuccess({ success: true });
      const idx = _reports.findIndex(
        (report) => report.getTemplateId() === action.reportTemplateId,
      );
      if (idx >= 0) {
        _reports.splice(idx, 1);
        EventFilterStore.emitChange();
      }
      break;
    }

    case constants.SET_EVENT_TYPES_FILTER: {
      const { eventTypes, showRevenue } = action;
      _showRevenue = showRevenue;
      setEventFilterState(eventTypes);

      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.SET_FEATURE_TAB_FILTERS: {
      const { eventTypesFilter = null, subtypeFilter, tagsFilter } = action;

      setEventFilterState(eventTypesFilter);

      setEventSubtypeFilterState(subtypeFilter);

      setEventTagsFilter(tagsFilter);

      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.CLEAR_FEATURE_TAB_FILTERS: {
      clearFeatureTabFilters();
      _showRevenue = false;
      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.SET_EVENT_SUBTYPES_FILTER: {
      const { eventSubtypes } = action;
      _showRevenue = false;
      setEventSubtypeFilterState(eventSubtypes);
      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.SET_EVENT_SUBTYPES_FILTER_REVENUE: {
      _showRevenue = true;
      // unset all other subtypes
      Object.keys(_eventSubtypeFilterState).forEach(
        (subtype) => (_eventSubtypeFilterState[subtype] = false),
      );
      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.SELECT_FILTER: {
      setErrorSuccess();

      const prevEventTypes = getEventTypes(_selectedReport);
      const nextEventTypes = getEventTypes(action.selectedReport);

      _selectedReport = action.selectedReport;

      if (nextEventTypes) {
        setEventFilterState(nextEventTypes);
      } else if (!nextEventTypes && prevEventTypes) {
        // Reset when previously selected filter is limited to specific event types
        setEventFilterState({
          transaction: true,
          motion: true,
          bookmark: true,
        });
      }

      EventFilterStore.emitChange();
      break;
    }

    case constants.CLEAR_SELECTED_REPORT: {
      _selectedReport = null;
      setErrorSuccess();
      EventFilterStore.emitChange();
      break;
    }

    case constants.USER_LOGGED_IN: {
      const { reportId, search, showBookmarks, showMotion, showTransaction } =
        AuthStore.getOriginalQuery();

      if (reportId) {
        _initialReportId = reportId;
      }

      if (search) {
        _searchString = search;
        _searchStringRegex = generateSearchRegex(_searchString);
      }

      const { transaction, bookmark, motion, saved } = getEventFilterState();
      setEventFilterState({
        transaction: showTransaction ?? transaction,
        bookmark: showBookmarks ?? bookmark,
        motion: showMotion ?? motion,
        saved,
      });
      break;
    }

    case constants.UPDATE_SEARCH_OPERATOR: {
      const { operator } = action;
      _searchString = _searchString.replaceAll(_searchOperator, operator);
      _searchOperator = operator;
      EventFilterStore.emitChange();
      break;
    }
  }
});

export default EventFilterStore;
